import { FormControl, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import FormMonitoring from 'components/FormMonitoring';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppColors } from 'resources/AppColors';
import CustomDialog from 'components/Dialogs/MasterDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePickerMonitoring from 'components/DatePickers/DatePickerMonitoring/DatePickerMonitoring';
import { CommonErrorsTexts, messagesTexts } from 'locale/es';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AppColors.PRIMARY,
      },
    },
    '& .MuiInputBase-root': {
      color: AppColors.PRIMARY,
    },
    '& .MuiInputLabel-root': {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const newMessageSchema = z
  .object({
    title: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, messagesTexts.formInputErrors.title),
    message: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, messagesTexts.formInputErrors.message),
    created: z.date({
      errorMap: (issue, { defaultError }) => ({
        message:
          issue.code === 'invalid_date'
            ? CommonErrorsTexts.date.invalidDate
            : issue.code === 'invalid_type'
            ? CommonErrorsTexts.date.date
            : defaultError,
      }),
    }),
  })
  .transform((data) => ({
    ...data,
    created: format(data.created, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
  }));

function MessageForm({ onSubmit, isEdit }) {
  const { entity } = useLocation().state;
  const { user } = useSelector((state) => state.userAuthentication);

  const navigate = useNavigate();

  const { handleSubmit, control, reset } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(newMessageSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      title: entity?.title || '',
      message: entity?.message || '',
      created: entity?.created ? new Date(entity?.created) : new Date(),
    },
  });

  const [formError, setFormError] = useState('');

  const selectedCenterId = useSelector(({ center }) => center)?.data?.id;

  const classes = useStyles();

  const dispatch = useDispatch();

  async function handleOnConfirmAddMessage(data) {
    try {
      const newMessage = isEdit
        ? {
            ...data,
            center: { id: selectedCenterId },
            id: entity.id,
          }
        : {
            ...data,
            center: { id: selectedCenterId },
            user: {
              id: entity.user.id,
              login: entity.user.login,
            },
            sender: {
              id: user.id,
              login: user.login,
            },
            visited: false,
          };

      await dispatch(onSubmit(newMessage));
      handleShowAddModal();
    } catch (error) {
      setFormError(error);
    }
  }

  function handleShowAddModal() {
    navigate('/missatges');
  }

  return (
    <CustomDialog
      title={messagesTexts.TITLE}
      show={true}
      handleShow={handleShowAddModal}
    >
      <FormMonitoring
        submitForm={handleSubmit(handleOnConfirmAddMessage)}
        resetFormInputs={reset}
        isEdit={isEdit}
      >
        {!isEdit && (
          <Controller
            control={control}
            name='created'
            render={({
              field: { onChange, onBlur, value, ref },
              fieldState: { error },
            }) => {
              return (
                <FormControl
                  sx={{ mt: 2, mb: 1 }}
                  className={classes.textFieldLabel}
                >
                  <DatePickerMonitoring
                    label={messagesTexts.DATE}
                    maxDate={new Date()}
                    inputRef={ref}
                    value={value}
                    setValue={onChange}
                    required={true}
                    textfieldProps={{
                      error: Boolean(error),
                      helperText: error?.message ?? '',
                      onBlur: onBlur,
                    }}
                  />
                </FormControl>
              );
            }}
          />
        )}
        <Controller
          control={control}
          name='title'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              label={messagesTexts.FORM_TITLE}
              margin='normal'
              variant='outlined'
              multiline
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ''}
            />
          )}
        />
        <Controller
          control={control}
          name='message'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              label={messagesTexts.FORM_MESSAGE}
              margin='normal'
              variant='outlined'
              multiline
              minRows={3}
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ''}
            />
          )}
        />
        <Typography variant='body' sx={{ color: 'red' }}>
          {formError && messagesTexts.formInputErrors.api_error}
        </Typography>
      </FormMonitoring>
    </CustomDialog>
  );
}

export default MessageForm;
